import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { partnerAdminApi, storageUtils, getPartnerSlug, type PartnerUser, type Partner, type MfaChallenge } from '../services/api'

interface AuthState {
  user: PartnerUser | null
  partner: Partner | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  mfaChallenge: MfaChallenge | null
  login: (email: string, password: string) => Promise<MfaChallenge | null>
  verifyMfa: (code: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => void
  clearError: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      partner: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      mfaChallenge: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const partnerSlug = getPartnerSlug()
          console.log('Partner Admin login attempt:', { email, partnerSlug })

          const response = await partnerAdminApi.auth.login(partnerSlug, email, password)
          console.log('Login response:', response)

          if ('mfa_required' in response && response.mfa_required) {
            set({
              isLoading: false,
              error: null,
              isAuthenticated: false,
              mfaChallenge: response
            })
            return response
          }

          const { access_token, refresh_token, user, partner } = response

          // Store auth data
          storageUtils.setAuthData(access_token, user, partner, refresh_token)

          // Update store state
          set({
            user: {
              id: user.id,
              email: user.email,
              name: user.name,
              role: user.role,
              partnerId: partner.id,
              permissions: []
            },
            partner: partner,
            token: access_token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            mfaChallenge: null
          })

          console.log('Partner Admin login successful')
          return null
        } catch (error: any) {
          console.error('Partner Admin login error:', error)

          let errorMessage = 'Erro ao fazer login. Tente novamente.'

          if (error.response?.status === 401) {
            errorMessage = 'Email ou senha inválidos.'
          } else if (error.response?.status === 404) {
            errorMessage = 'Parceiro não encontrado.'
          } else if (error.message?.includes('Network Error')) {
            errorMessage = 'Erro de conexão. Verifique se o servidor está rodando.'
          }

          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false,
            mfaChallenge: null
          })

          throw new Error(errorMessage)
        }
      },

      verifyMfa: async (code: string) => {
        const challenge = get().mfaChallenge
        if (!challenge) {
          throw new Error('MFA não iniciado')
        }

        set({ isLoading: true, error: null })
        try {
          const partnerSlug = getPartnerSlug()
          const response = await partnerAdminApi.auth.verifyMfa(
            partnerSlug,
            challenge.temp_token,
            code
          )

          const { access_token, refresh_token, user, partner } = response

          storageUtils.setAuthData(access_token, user, partner, refresh_token)

          set({
            user: {
              id: user.id,
              email: user.email,
              name: user.name,
              role: user.role,
              partnerId: partner.id,
              permissions: []
            },
            partner: partner,
            token: access_token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            mfaChallenge: null
          })
        } catch (error: any) {
          console.error('Partner Admin MFA error:', error)
          const errorMessage = error.response?.data?.message || 'Erro ao validar 2FA.'
          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false
          })
          throw new Error(errorMessage)
        }
      },

      logout: async () => {
        try {
          const partnerSlug = getPartnerSlug()
          await partnerAdminApi.auth.logout(partnerSlug)
        } catch (error) {
          console.warn('Logout API call failed, clearing local data anyway:', error)
        }

        // Always clear local data
        storageUtils.clearAuthData()

        set({
          user: null,
          partner: null,
          token: null,
          isAuthenticated: false,
          error: null,
          mfaChallenge: null
        })
      },

      checkAuth: () => {
        const token = storageUtils.getAuthToken()
        const user = storageUtils.getUser()
        const partner = storageUtils.getPartner()
        const isAuthenticated = storageUtils.isAuthenticated()

        console.log('checkAuth:', {
          hasToken: !!token,
          hasUser: !!user,
          hasPartner: !!partner,
          isAuthenticated
        })

        if (token && user && isAuthenticated) {
          set({
            token,
            user,
            partner,
            isAuthenticated: true
          })
        } else {
          set({
            token: null,
            user: null,
            partner: null,
            isAuthenticated: false,
            mfaChallenge: null
          })
        }
      },

      clearError: () => {
        set({ error: null })
      }
    }),
    {
      name: 'partner-admin-auth-storage',
      partialize: (state) => ({
        user: state.user,
        partner: state.partner,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      }),
    }
  )
)
